// src/crm/ugc/UgcPage.tsx
//
// Page « Programme UGC » du CRM (spec § 6) : titre, sept onglets (dont « Pro à activer », « Montants à
// fixer », les montants pour 1 000 vues des membres, et « Messages », le chat avec les créateurs) avec le nombre d'éléments à
// traiter, puis l'onglet courant (routes enfants de /crm/ugc). Les compteurs sont ceux de l'app
// Basalt Lab, résolue par son slug (l'id diffère entre local et production).

import { useQuery } from '@tanstack/react-query'
import { NavLink, Outlet } from 'react-router-dom'
import { api } from '../../lib/api'
import type { App } from '../../lib/types'
import { ugcActionCounts, type UgcOverview } from '../../lib/ugcTypes'
import { useUgcOverview } from './hooks'
import './ugc.css'

const BASALT_LAB_SLUG = 'basalt-lab'

const TABS: readonly { label: string; to: string; count: (o: UgcOverview) => number | null }[] = [
  { label: 'Candidatures', to: 'candidatures', count: (o) => ugcActionCounts(o).applications },
  { label: 'Vidéos', to: 'videos', count: (o) => ugcActionCounts(o).videos },
  { label: 'Publications et paiements', to: 'paiements', count: (o) => ugcActionCounts(o).payments },
  { label: 'Parrainages', to: 'parrainages', count: () => null },
  { label: 'Pro à activer', to: 'pro', count: (o) => ugcActionCounts(o).members },
  { label: 'Montants à fixer', to: 'montants', count: (o) => ugcActionCounts(o).rates },
  { label: 'Messages', to: 'messages', count: (o) => ugcActionCounts(o).messages },
]

export function UgcPage() {
  const apps = useQuery<{ apps: App[] }, Error>({
    queryKey: ['apps'],
    queryFn: () => api.get<{ apps: App[] }>('/api/apps'),
  })
  const appId = apps.data?.apps.find((a) => a.slug === BASALT_LAB_SLUG)?.id ?? null
  const overview = useUgcOverview(appId)
  // App introuvable par son slug, liste des apps ou compteurs en échec : les onglets restent, sans chiffres.
  // Les compteurs se relisent toutes les 30 s (hooks.ts) ; une relecture ratée garde les derniers chiffres.
  const countsFailed = apps.isError || (overview.isError && !overview.data) || (apps.isSuccess && appId === null)

  return (
    <div className="ugc">
      <div className="ugc-head">
        <h1 className="ugc-title">Programme UGC</h1>
        {countsFailed && <p className="ugc-error">Compteurs indisponibles.</p>}
      </div>
      <nav className="ugc-tabs" aria-label="Onglets du programme UGC">
        {TABS.map((tab) => {
          const count = overview.data ? tab.count(overview.data) : null
          const shown = count !== null && count > 0
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => (isActive ? 'ugc-tab ugc-tab--on' : 'ugc-tab')}
              aria-label={shown ? `${tab.label}, ${count} à traiter` : undefined}
            >
              {tab.label}
              {shown && (
                <span className="ugc-count" aria-hidden="true">
                  {count}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>
      <Outlet />
    </div>
  )
}

